import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { API, useAuth } from "../App";
import { Leaf, ArrowRight, Sparkles, Mail, Lock, User } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [inviter, setInviter] = useState(null);
  const [tab, setTab] = useState("google");
  const [mode, setMode] = useState("login"); // login | register | forgot
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [busy, setBusy] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const inviteCode = params.get("invite");

  useEffect(() => { if (user) navigate("/profilo", { replace: true }); }, [user, navigate]);

  useEffect(() => {
    if (!inviteCode) return;
    sessionStorage.setItem("tc_invite", inviteCode);
    axios.get(`${API}/invitations/${inviteCode}`)
      .then(r => setInviter(r.data))
      .catch(() => setInviter({ used: false, inviter: null, invalid: true }));
  }, [inviteCode]);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogle = () => {
    const redirectUrl = window.location.origin + "/profilo";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "forgot") {
        await axios.post(`${API}/auth/forgot-password`, { email: form.email });
        setForgotSent(true);
        toast.success("Se l'email è registrata, riceverai un link tra poco.");
      } else {
        const url = mode === "register" ? `${API}/auth/register` : `${API}/auth/login`;
        const payload = mode === "register"
          ? { email: form.email, password: form.password, name: form.name }
          : { email: form.email, password: form.password };
        await axios.post(url, payload);
        await refresh();
        toast.success(mode === "register" ? "Benvenuta nella rete" : "Bentornata");
        navigate("/profilo", { replace: true });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  return (
    <main className="min-h-[80vh] flex items-center justify-center px-6 py-16" data-testid="login-page">
      <div className="max-w-md w-full">
        <Leaf className="w-10 h-10 text-tc-primary mx-auto mb-6" />

        {inviter?.inviter && !inviter.used && (
          <div className="bg-tc-surface rounded-3xl p-6 mb-8 border border-tc-secondary/30 text-center" data-testid="inviter-card">
            <div className="flex items-center justify-center gap-3 mb-3">
              {inviter.inviter.picture && <img src={inviter.inviter.picture} alt="" className="w-12 h-12 rounded-full border-2 border-tc-accent/40" />}
              <Sparkles className="w-5 h-5 text-tc-secondary" />
            </div>
            <p className="text-tc-primary font-serif text-xl italic">
              <strong className="not-italic">{inviter.inviter.name}</strong> ti accoglie<br/> nella rete.
            </p>
          </div>
        )}

        <h1 className="font-serif text-5xl text-tc-primary leading-tight text-center">
          Unisciti alla<br/><em className="text-tc-secondary italic">comunità</em>
        </h1>
        <p className="text-tc-muted mt-6 mb-8 leading-relaxed text-center">
          Per proporre eventi, aggiungere realtà sulla mappa, scrivere nel forum.
        </p>

        {/* Tab switcher */}
        <div className="flex bg-tc-surface rounded-full p-1 mb-6" data-testid="auth-tabs">
          <button onClick={() => setTab("google")} data-testid="tab-google"
            className={`flex-1 py-2.5 text-sm rounded-full transition-colors ${tab === 'google' ? 'bg-tc-bg text-tc-primary shadow-sm' : 'text-tc-muted'}`}>
            Google
          </button>
          <button onClick={() => setTab("email")} data-testid="tab-email"
            className={`flex-1 py-2.5 text-sm rounded-full transition-colors ${tab === 'email' ? 'bg-tc-bg text-tc-primary shadow-sm' : 'text-tc-muted'}`}>
            Email e password
          </button>
        </div>

        {tab === "google" ? (
          <button onClick={handleGoogle} className="btn-primary inline-flex items-center gap-2 w-full justify-center" data-testid="google-login-btn">
            Entra con Google <ArrowRight className="w-4 h-4" />
          </button>
        ) : (
          <form onSubmit={handleEmailSubmit} className="space-y-4" data-testid="email-form">
            <div className="flex gap-1 text-xs mb-3" data-testid="mode-switch">
              <button type="button" onClick={() => { setMode("login"); setForgotSent(false); }} data-testid="mode-login"
                className={`flex-1 py-1.5 rounded-full ${mode === 'login' ? 'bg-tc-primary text-tc-bg' : 'text-tc-muted'}`}>
                Accedi
              </button>
              <button type="button" onClick={() => { setMode("register"); setForgotSent(false); }} data-testid="mode-register"
                className={`flex-1 py-1.5 rounded-full ${mode === 'register' ? 'bg-tc-primary text-tc-bg' : 'text-tc-muted'}`}>
                Registrati
              </button>
            </div>

            {mode === "forgot" ? (
              forgotSent ? (
                <div className="bg-tc-surface rounded-2xl p-6 border border-tc/40 text-center" data-testid="forgot-sent">
                  <p className="text-tc-primary font-serif text-lg italic mb-3">Controlla la tua email.</p>
                  <p className="text-sm text-tc-muted">Se l'indirizzo è registrato, ti abbiamo inviato un link valido per 1 ora.</p>
                </div>
              ) : (
                <>
                  <p className="text-sm text-tc-muted mb-4">Inserisci la tua email: ti manderemo un link per scegliere una nuova password.</p>
                  <Field icon={<Mail className="w-4 h-4" />} placeholder="email@esempio.it" type="email"
                    value={form.email} onChange={v => setForm({ ...form, email: v })} testid="forgot-email" required />
                </>
              )
            ) : (
              <>
                {mode === "register" && (
                  <Field icon={<User className="w-4 h-4" />} placeholder="Il tuo nome" type="text"
                    value={form.name} onChange={v => setForm({ ...form, name: v })} testid="email-name" required />
                )}
                <Field icon={<Mail className="w-4 h-4" />} placeholder="email@esempio.it" type="email"
                  value={form.email} onChange={v => setForm({ ...form, email: v })} testid="email-email" required />
                <Field icon={<Lock className="w-4 h-4" />} placeholder={mode === 'register' ? "Password (min 8 caratteri)" : "Password"} type="password"
                  value={form.password} onChange={v => setForm({ ...form, password: v })} testid="email-password" required />
              </>
            )}

            {!forgotSent && (
              <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="email-submit">
                {busy ? "Un istante…" : mode === "register" ? "Registrati" : mode === "forgot" ? "Invia link" : "Accedi"}
              </button>
            )}

            <div className="text-center text-xs">
              {mode === "forgot" ? (
                <button type="button" onClick={() => { setMode("login"); setForgotSent(false); }} className="text-tc-muted hover:text-tc-primary underline" data-testid="back-to-login">
                  Torna al login
                </button>
              ) : mode === "login" ? (
                <button type="button" onClick={() => setMode("forgot")} className="text-tc-muted hover:text-tc-primary underline" data-testid="forgot-link">
                  Hai dimenticato la password?
                </button>
              ) : null}
            </div>
          </form>
        )}

        <p className="text-xs text-tc-muted mt-8 text-center">
          <Link to="/" className="underline">Torna indietro</Link>
        </p>
      </div>
    </main>
  );
}

function Field({ icon, placeholder, type, value, onChange, testid, required }) {
  return (
    <div className="flex items-center gap-3 border-b border-tc focus-within:border-tc-primary py-2">
      <span className="text-tc-muted">{icon}</span>
      <input type={type} placeholder={placeholder} value={value} required={required}
        onChange={e => onChange(e.target.value)}
        className="flex-1 bg-transparent outline-none text-sm" data-testid={testid} />
    </div>
  );
}
